import { NavLink } from "@/components/NavLink";
import { useAuthContext } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { 
  MessageSquare, 
  FolderOpen, 
  LayoutDashboard,
  Building2,
  Users,
  BarChart3
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useMemo } from "react";

interface NavItem {
  labelKey: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  roles?: ('super_admin' | 'company_admin' | 'user')[];
}

const baseNavItems: NavItem[] = [
  {
    labelKey: "nav.generator",
    href: "/",
    icon: MessageSquare,
  },
  {
    labelKey: "nav.projects",
    href: "/projects",
    icon: FolderOpen,
    roles: ['company_admin'],
  },
];

const companyAdminNav: NavItem[] = [
  {
    labelKey: "nav.dashboard",
    href: "/dashboard",
    icon: LayoutDashboard,
    roles: ['company_admin'],
  },
];

const superAdminNavItems: NavItem[] = [
  {
    labelKey: "nav.adminPanel",
    href: "/admin",
    icon: LayoutDashboard,
  },
  {
    labelKey: "nav.companies",
    href: "/admin/companies",
    icon: Building2,
  },
  {
    labelKey: "nav.users",
    href: "/admin/users",
    icon: Users,
  },
  {
    labelKey: "nav.stats",
    href: "/admin/stats",
    icon: BarChart3,
  },
];

export function Navigation() {
  const { user } = useAuthContext();
  const { t } = useTranslation('common');
  const userRole = user?.role;

  const visibleNavItems = useMemo(() => {
    if (userRole === 'super_admin') {
      return [];
    }
    
    return baseNavItems.filter(item => {
      if (!item.roles) return true;
      return userRole && item.roles.includes(userRole);
    });
  }, [userRole]);

  const showCompanyAdminNav = userRole === 'company_admin';
  const showSuperAdminNav = userRole === 'super_admin';

  const renderNavItem = (item: NavItem) => (
    <NavLink
      key={item.href}
      to={item.href}
      className={cn(
        "flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-all",
        "text-muted-foreground hover:bg-accent hover:text-foreground"
      )}
      activeClassName="bg-secondary text-foreground"
    >
      <item.icon className="h-4 w-4" />
      {t(item.labelKey)}
    </NavLink>
  );

  return (
    <nav className="border-b border-border bg-card/50">
      <div className="container">
        <div className="flex items-center gap-1 overflow-x-auto py-2">
          {showSuperAdminNav && superAdminNavItems.map(renderNavItem)}
          {visibleNavItems.map(renderNavItem)}
          {showCompanyAdminNav && companyAdminNav.map(renderNavItem)}
        </div>
      </div>
    </nav>
  );
}
